import { useState } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'
import { Button } from '../button'
import { Gate } from '../gate'
import { Spinner } from '../spinner'
import { CustomSelect } from '../custom-select'
import { InlineHelp } from '../inline-help'
import { useToast } from '../toast'
import { usePermissions } from 'src/hooks/use-permissions'
import { SelectOption } from 'src/services/base-model'
import { numberApi } from 'src/services/numbers/api'
import { numberQueries } from 'src/services/numbers/queries'
import { AvailableNumber } from 'src/services/numbers/models'
import { whatsappTemplateQueries } from 'src/services/whatsapp/queries'
import { Stepper, StepperStep } from '../stepper'
import {
  deriveFailedNumber,
  deriveSetupStep,
  isSetupStepComplete,
  SETUP_STEP_ORDER,
  setupStepEnum,
} from 'src/services/numbers/setup-wizard'

const COUNTRY_OPTIONS: SelectOption[] = [
  { label: 'US - United States', value: 'US' },
  { label: 'CO - Colombia', value: 'CO' },
  { label: 'MX - Mexico', value: 'MX' },
  { label: 'CA - Canada', value: 'CA' },
  { label: 'GB - United Kingdom', value: 'GB' },
  { label: 'ES - Spain', value: 'ES' },
  { label: 'BR - Brazil', value: 'BR' },
  { label: 'AR - Argentina', value: 'AR' },
]

const STEP_LABEL_KEYS = {
  [setupStepEnum.GET_NUMBER]: 'commsSetup.steps.getNumber',
  [setupStepEnum.GETTING_READY]: 'commsSetup.steps.gettingReady',
  [setupStepEnum.READY]: 'commsSetup.steps.ready',
} as const

interface CommsSetupWizardProps {
  orgId: string
}

/**
 * Guided setup that walks an admin from no number to ready. The current step is
 * derived from org number state (see `deriveSetupStep`) so re-entry always
 * resumes at the right place, and only the current step's decision is shown —
 * one decision at a time.
 */
export const CommsSetupWizard = ({ orgId }: CommsSetupWizardProps) => {
  const { t } = useTranslation()
  const { hasPermission } = usePermissions()
  const canManage = hasPermission('numbers.manage')

  const { data: numbers, isLoading } = useQuery({
    ...numberQueries.list(orgId),
    enabled: Boolean(orgId) && canManage,
  })

  const currentStep = deriveSetupStep(numbers ?? [])
  const failedNumber = deriveFailedNumber(numbers ?? [])

  const steps: StepperStep[] = SETUP_STEP_ORDER.map((step) => ({
    id: step,
    label: t(STEP_LABEL_KEYS[step]),
    complete: isSetupStepComplete(step, currentStep),
    'data-testid': `setup-step-${step}`,
  }))

  return (
    <Gate permission="numbers.manage">
      <div
        className="rounded-lg border border-border bg-surface p-6 shadow-sm"
        data-testid="comms-setup-wizard"
      >
        <div className="mb-4">
          <h3 className="text-lg font-medium text-text-primary">{t('commsSetup.title')}</h3>
          <p className="mt-1 text-sm text-text-placeholder">{t('commsSetup.subtitle')}</p>
        </div>

        <Stepper
          steps={steps}
          currentId={currentStep}
          aria-label={t('commsSetup.stepperAriaLabel')}
          className="mb-6"
          data-testid="comms-setup-stepper"
        />

        {isLoading ? (
          <div className="flex items-center justify-center py-8" data-testid="setup-loading">
            <Spinner size="md" />
          </div>
        ) : currentStep === setupStepEnum.GET_NUMBER ? (
          <GetNumberStep orgId={orgId} failureReason={failedNumber?.failureReason ?? null} />
        ) : currentStep === setupStepEnum.GETTING_READY ? (
          <GettingReadyStep />
        ) : (
          <ReadyStep orgId={orgId} />
        )}
      </div>
    </Gate>
  )
}

// ---------------------------------------------------------------------------
// Step "Get a number" — pick a country, then choose a number. One plain line
// states it's a cancellable monthly subscription; prices show per option. No
// WhatsApp/Meta jargon at this step.
// ---------------------------------------------------------------------------

const GetNumberStep = ({
  orgId,
  failureReason,
}: {
  orgId: string
  /** Set when a prior number's review failed — surfaces a plain-language notice
   * so re-entry is a guided retry, not an unexplained empty step. */
  failureReason: string | null
}) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [country, setCountry] = useState<SelectOption[]>([COUNTRY_OPTIONS[0]])
  const [results, setResults] = useState<AvailableNumber[] | null>(null)

  const search = useMutation({
    mutationFn: () =>
      numberApi.csc.searchAvailable({ organization: orgId, country: country[0]?.value ?? 'US' }),
    onSuccess: (data) => setResults(data),
    onError: () =>
      toast({
        title: t('commsSetup.getNumber.searchFailed'),
        description: '',
        type: 'error',
      }),
  })

  const subscribe = useMutation({
    mutationFn: (phoneNumber: string) =>
      numberApi.csc.subscribe({ organization: orgId, phoneNumber }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: numberQueries.all() })
      toast({
        title: t('whatsappNumbers.numberRequestedTitle'),
        description: t('whatsappNumbers.numberRequestedDesc'),
        type: 'success',
      })
    },
    onError: (error: any) => {
      const message = error?.response?.data?.detail || t('whatsappNumbers.subscribeFailed')
      toast({ title: t('common.error'), description: message, type: 'error' })
    },
  })

  return (
    <div data-testid="setup-get-number">
      {failureReason !== null && (
        <div
          className="border-warning/40 bg-warning/10 mb-4 flex gap-3 rounded-md border p-3"
          role="alert"
          data-testid="setup-review-failed"
        >
          <ExclamationTriangleIcon
            className="mt-0.5 h-5 w-5 shrink-0 text-warning"
            aria-hidden="true"
          />
          <div className="text-sm">
            <p className="font-medium text-text-primary">{t('commsSetup.reviewFailed.heading')}</p>
            <p className="mt-0.5 text-text-secondary" data-testid="setup-review-failed-reason">
              {failureReason || t('commsSetup.reviewFailed.genericReason')}
            </p>
            <p className="mt-1 text-text-placeholder">{t('commsSetup.reviewFailed.retryHint')}</p>
          </div>
        </div>
      )}
      <h4 className="text-base font-semibold text-text-primary">
        {t('commsSetup.getNumber.heading')}
      </h4>
      <InlineHelp
        className="mt-2"
        text={t('commsSetup.getNumber.subscriptionLine')}
        detail={t('commsSetup.getNumber.subscriptionDetail')}
        data-testid="setup-subscription-line"
      />

      <div className="mt-4 flex items-end gap-3">
        <div className="flex-1">
          <label className="mb-1 block text-sm font-medium text-gray-700">
            {t('commsSetup.getNumber.countryLabel')}
          </label>
          <CustomSelect
            options={COUNTRY_OPTIONS}
            values={country}
            onChange={(values) => setCountry(values)}
            multi={false}
            clearable={false}
            searchable
          />
        </div>
        <Button
          variant="secondary"
          onClick={() => search.mutate()}
          isLoading={search.isPending}
          data-testid="setup-find-numbers"
        >
          {t('commsSetup.getNumber.searchAction')}
        </Button>
      </div>

      {results && results.length === 0 && (
        <div
          className="mt-4 rounded-md border border-border p-4 text-center text-sm text-text-placeholder"
          data-testid="setup-no-results"
        >
          {t('commsSetup.getNumber.noResults')}
        </div>
      )}

      {results && results.length > 0 && (
        <div className="mt-4 max-h-72 divide-y divide-border overflow-y-auto rounded-md border border-border">
          {results.map((available) => (
            <div
              key={available.phoneNumber}
              className="flex items-center justify-between px-4 py-3"
              data-testid={`setup-available-${available.phoneNumber}`}
            >
              <div>
                <div className="text-sm font-medium text-text-primary">{available.phoneNumber}</div>
                <div className="text-xs text-text-placeholder">
                  {available.monthlyPrice
                    ? `${available.priceUnit ?? ''} ${available.monthlyPrice}/mo`.trim()
                    : t('commsSetup.getNumber.priceUnavailable')}
                </div>
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={() => subscribe.mutate(available.phoneNumber)}
                isLoading={subscribe.isPending}
                data-testid={`setup-choose-${available.phoneNumber}`}
              >
                {t('commsSetup.getNumber.choose')}
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Step "Getting it ready" — a waiting state. One sentence explains the review;
// the numbers query polls while settling, so this advances on its own.
// ---------------------------------------------------------------------------

const GettingReadyStep = () => {
  const { t } = useTranslation()
  return (
    <div
      className="flex flex-col items-center gap-3 py-6 text-center"
      data-testid="setup-getting-ready"
    >
      <Spinner size="lg" />
      <p className="max-w-md text-sm text-text-secondary">
        {t('commsSetup.gettingReady.explanation')}
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Step "You're set" — confirms the number is active and the default welcome
// message is approved, then points the team to the messaging hub.
// ---------------------------------------------------------------------------

const ReadyStep = ({ orgId }: { orgId: string }) => {
  const { t } = useTranslation()
  const { data: approved } = useQuery(whatsappTemplateQueries.approved(orgId))
  const hasApprovedWelcome = Boolean(approved?.results?.some((tpl) => tpl.isDefault))

  return (
    <div className="flex flex-col items-center gap-3 py-6 text-center" data-testid="setup-ready">
      <CheckCircleIcon className="h-10 w-10 text-green-500" aria-hidden="true" />
      <p className="max-w-md text-sm text-text-secondary" data-testid="setup-ready-body">
        {hasApprovedWelcome
          ? t('commsSetup.ready.body')
          : t('commsSetup.ready.bodyPendingTemplate')}
      </p>
      <Link to="/chat-clients">
        <Button
          variant="primary"
          icon={<ChatBubbleLeftRightIcon className="h-4 w-4" />}
          data-testid="setup-go-to-messages"
        >
          {t('commsSetup.ready.primaryAction')}
        </Button>
      </Link>
    </div>
  )
}
